import { Fragment, useState, useEffect, useCallback } from "react";
import { AlertTriangle, BarChart3, Check, ChevronDown, ChevronRight, Cpu, RefreshCw, Wallet } from "lucide-react";
import { usePlatformAdmin } from "../hooks/usePlatformAdmin";
import { KIND_LABELS } from "../components/AdminAlertsBanner";
import * as api from "../lib/api";

// @lat: [[alerts#Model Switching#Admin page]]
const PRIMARY = "#018D4C";
const MODEL_FIELDS = [
  { key: "ollama_model", label: "Main model", hint: "Outlines, labels, tagging, recommendations — every LLM feature" },
  { key: "ollama_review_model", label: "Review model", hint: "QA pass on generated outlines before publishing" },
];

function ModelsCard() {
  const [data, setData] = useState(null);
  const [picks, setPicks] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    api.getAdminModels().then(d => { setData(d); setPicks(d.current); }).catch(e => setMsg({ error: true, text: e.message }));
  }, []);
  useEffect(load, [load]);

  const save = async (body) => {
    setSaving(true);
    setMsg(null);
    try {
      const r = await api.switchAdminModels(body);
      setData(d => ({ ...d, current: r.current }));
      setPicks(r.current);
      setMsg({ text: "Saved — applies to the next LLM call." });
    } catch (e) {
      setMsg({ error: true, text: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="bg-white rounded-xl border border-zinc-100 p-5 shadow-sm text-sm text-zinc-500">Loading models…</div>;
  const offered = new Set(data.available.map(m => m.name));
  const changed = MODEL_FIELDS.filter(f => picks[f.key] !== data.current[f.key]);

  return (
    <div className="bg-white rounded-xl border border-zinc-100 p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <Cpu size={18} style={{ color: PRIMARY }} />
        <h2 className="font-semibold text-zinc-900">Ollama Cloud models</h2>
        <button onClick={load} className="ml-auto text-zinc-400 hover:text-zinc-600" title="Refresh list"><RefreshCw size={14} /></button>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        Live list from your Ollama account ({data.available.length} chat models, newest first). Env vars are the default; a switch here overrides them without a redeploy.
      </p>
      {data.error && <p className="text-xs text-red-500 mb-3">Could not list models: {data.error}</p>}
      <div className="space-y-4">
        {MODEL_FIELDS.map(f => {
          const current = data.current[f.key];
          const missing = data.available.length > 0 && !offered.has(current);
          return (
            <div key={f.key}>
              <label className="block text-sm font-medium text-zinc-900">{f.label}</label>
              <p className="text-xs text-zinc-400 mb-1">{f.hint} · env default <code>{data.defaults[f.key]}</code></p>
              <div className="flex items-center gap-2">
                <select
                  value={picks[f.key] || ""}
                  onChange={e => setPicks(p => ({ ...p, [f.key]: e.target.value }))}
                  className="flex-1 rounded-lg border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#018D4C]"
                >
                  {missing && <option value={current}>{current} (no longer offered)</option>}
                  {data.available.map(m => (
                    <option key={m.name} value={m.name}>
                      {m.name}{m.capabilities.includes("thinking") ? " · thinking" : ""}{m.modified_at ? ` · ${m.modified_at.slice(0, 10)}` : ""}
                    </option>
                  ))}
                </select>
                {current !== data.defaults[f.key] && (
                  <button onClick={() => save({ reset: [f.key] })} disabled={saving} className="text-xs text-zinc-500 hover:text-zinc-700">Reset</button>
                )}
              </div>
              {missing && (
                <p className="mt-1 flex items-center gap-1 text-xs text-red-500">
                  <AlertTriangle size={12} /> <code>{current}</code> is not offered any more — calls are failing. Pick a replacement.
                </p>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => save(Object.fromEntries(changed.map(f => [f.key, picks[f.key]])))}
          disabled={saving || changed.length === 0}
          className="rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
          style={{ background: PRIMARY }}
        >
          {saving ? "Saving…" : "Switch model"}
        </button>
        {msg && <span className={`text-xs ${msg.error ? "text-red-500" : "text-green-600"}`}>{msg.text}</span>}
      </div>
    </div>
  );
}

const USD = (n) => (n < 0.01 && n > 0 ? "<$0.01" : `$${n.toFixed(2)}`);
const NUM = (n) => n.toLocaleString();

// @lat: [[usage#Usage Metering#Provider budgets]]
const STATUS_STYLE = {
  empty:     { label: "Used up",   bar: "#DC2626", text: "text-red-600" },
  low:       { label: "Low",       bar: "#F59E0B", text: "text-amber-600" },
  ok:        { label: "OK",        bar: PRIMARY,   text: "text-green-700" },
  no_budget: { label: "No top-up recorded", bar: "#D4D4D8", text: "text-zinc-400" },
};
const today = () => new Date().toISOString().slice(0, 10);

function BudgetForm({ provider, budget, onSaved }) {
  const [amount, setAmount] = useState(budget ? String(budget.amount_usd) : "");
  const [since, setSince] = useState(budget?.since?.slice(0, 10) || today());
  const [note, setNote] = useState(budget?.note || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const save = async (remove = false) => {
    setSaving(true);
    setErr(null);
    try {
      await api.putAdminBudget(remove ? { provider } : { provider, amount_usd: Number(amount), since, note });
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
      setSaving(false);
    }
  };
  return (
    <div className="mt-2 flex flex-wrap items-end gap-2 rounded-lg bg-zinc-50 p-3 text-xs">
      <label className="flex flex-col gap-1">Balance after top-up (USD)
        <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
          className="w-32 rounded-md border border-zinc-200 px-2 py-1" />
      </label>
      <label className="flex flex-col gap-1">Since
        <input type="date" value={since} onChange={e => setSince(e.target.value)} className="rounded-md border border-zinc-200 px-2 py-1" />
      </label>
      <label className="flex flex-1 flex-col gap-1">Note
        <input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Pro plan $60 included"
          className="min-w-32 rounded-md border border-zinc-200 px-2 py-1" />
      </label>
      <button onClick={() => save()} disabled={saving || !(Number(amount) > 0)}
        className="rounded-md px-3 py-1.5 font-medium text-white disabled:opacity-40" style={{ background: PRIMARY }}>
        {saving ? "Saving…" : "Save"}
      </button>
      {budget && <button onClick={() => save(true)} disabled={saving} className="px-2 py-1.5 text-zinc-500 hover:text-zinc-800">Remove</button>}
      {err && <span className="w-full text-red-500">{err}</span>}
    </div>
  );
}

// What the platform has left at each provider, so an empty account is topped up before
// every AI feature starts failing. Spend is our estimate from TOKEN_RATES, not the provider's bill.
function ProviderBudgetsCard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    api.getAdminUsage(30)
      .then(d => { setData(d); setError(null); setEditing(null); })
      .catch(e => { setData({ providers: [] }); setError(e.response?.data?.detail || e.message); });
  }, []);
  useEffect(load, [load]);

  return (
    <div className="bg-white rounded-xl border border-zinc-100 p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <Wallet size={18} style={{ color: PRIMARY }} />
        <h2 className="font-semibold text-zinc-900">Provider balances</h2>
        <button onClick={load} className="ml-auto text-zinc-400 hover:text-zinc-600" title="Refresh"><RefreshCw size={14} /></button>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        Record each top-up; spend since then is our estimate from the rate table, not the provider&apos;s bill.
        An alert opens at 80% used and when used up. Burn rate = last 7 days.
      </p>
      {data === null && <p className="text-sm text-zinc-500">Loading…</p>}
      {error && <p className="text-sm text-red-500">{error}</p>}
      {data && !error && data.providers?.length === 0 && <p className="text-sm text-zinc-500">No provider calls in the last 30 days.</p>}
      <ul className="space-y-3">
        {data?.providers?.map(p => {
          const st = STATUS_STYLE[p.status];
          const b = p.budget;
          return (
            <li key={p.provider} className="rounded-lg border border-zinc-100 p-3">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <code className="text-sm font-semibold text-zinc-900">{p.provider}</code>
                <span className={`text-xs font-semibold ${st.text}`}>{st.label}</span>
                <span className="text-xs text-zinc-500">
                  30d: {USD(p.cost_usd)} · {NUM(p.calls)} calls · {USD(p.burn_per_day)}/day
                  {p.unpriced_calls > 0 && <span className="text-amber-600"> · {NUM(p.unpriced_calls)} unpriced</span>}
                </span>
                <button onClick={() => setEditing(editing === p.provider ? null : p.provider)}
                  className="ml-auto text-xs font-medium hover:underline" style={{ color: PRIMARY }}>
                  {b ? "Record top-up" : "Set balance"}
                </button>
              </div>
              {b && (
                <>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-zinc-100">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, b.used_share * 100)}%`, background: st.bar }} />
                  </div>
                  <p className="mt-1 text-xs text-zinc-500">
                    <b className="text-zinc-800">{USD(Math.max(0, b.remaining_usd))}</b> left of {USD(b.amount_usd)} since {b.since.slice(0, 10)}
                    {" "}· spent {USD(b.spent_usd)} ({Math.round(b.used_share * 100)}%)
                    {b.days_left != null && <> · ~{Math.round(b.days_left)} days left</>}
                    {b.unpriced_calls > 0 && <span className="text-amber-600"> · {NUM(b.unpriced_calls)} unpriced calls not counted</span>}
                    {b.note && <span className="text-zinc-400"> · {b.note}</span>}
                  </p>
                </>
              )}
              {editing === p.provider && <BudgetForm provider={p.provider} budget={b} onSaved={load} />}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function UsageCard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    setData(null);
    api.getAdminUsage(days)
      .then(d => { setData(d); setError(null); })
      .catch(e => { setData({ orgs: [] }); setError(e.response?.data?.detail || e.message); });
  }, [days]);
  useEffect(load, [load]);

  return (
    <div className="bg-white rounded-xl border border-zinc-100 p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 size={18} style={{ color: PRIMARY }} />
        <h2 className="font-semibold text-zinc-900">Usage by organization</h2>
        <select value={days} onChange={e => setDays(Number(e.target.value))} className="ml-auto rounded-md border border-zinc-200 px-2 py-1 text-xs">
          {[1, 7, 30, 90].map(d => <option key={d} value={d}>Last {d} day{d > 1 ? "s" : ""}</option>)}
        </select>
        <button onClick={load} className="text-zinc-400 hover:text-zinc-600" title="Refresh"><RefreshCw size={14} /></button>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        Our provider cost per org from <code>usage_events</code>. Unpriced calls use a model with no rate in <code>TOKEN_RATES</code>.
      </p>
      {data === null && <p className="text-sm text-zinc-500">Loading…</p>}
      {error && <p className="text-sm text-red-500">{error}</p>}
      {data && !error && (
        <>
          <p className="text-sm text-zinc-700 mb-3">
            <b>{USD(data.total_cost_usd)}</b> across {NUM(data.total_calls)} calls · {data.orgs.length} org(s)
            {data.truncated && <span className="text-amber-600"> · truncated at 100k rows</span>}
          </p>
          {data.orgs.length === 0 && <p className="text-sm text-zinc-500">No usage in this period.</p>}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              {data.orgs.length > 0 && (
                <thead>
                  <tr className="text-left text-xs text-zinc-400">
                    <th className="py-1 font-normal">Organization</th>
                    <th className="py-1 font-normal text-right">Calls</th>
                    <th className="py-1 font-normal text-right">Tokens in / out</th>
                    <th className="py-1 font-normal text-right">Cost</th>
                    <th className="py-1 font-normal text-right">Last call</th>
                  </tr>
                </thead>
              )}
              <tbody>
                {data.orgs.map(o => {
                  const key = o.org_id || "none";
                  const expanded = open === key;
                  return (
                    <Fragment key={key}>
                      <tr onClick={() => setOpen(expanded ? null : key)} className="cursor-pointer border-t border-zinc-100 hover:bg-zinc-50">
                        <td className="py-2">
                          <span className="flex items-center gap-1">
                            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            <span className="font-medium text-zinc-900">{o.org_id ? (o.name || o.org_id) : "No org (startup / system jobs)"}</span>
                          </span>
                          {o.name && <span className="ml-5 text-xs text-zinc-400">{o.org_id}</span>}
                        </td>
                        <td className="py-2 text-right">{NUM(o.calls)}</td>
                        <td className="py-2 text-right text-zinc-500">{NUM(o.input_tokens)} / {NUM(o.output_tokens)}</td>
                        <td className="py-2 text-right font-medium">
                          {USD(o.cost_usd)}
                          {o.unpriced_calls > 0 && <span className="block text-xs font-normal text-amber-600">+{NUM(o.unpriced_calls)} unpriced</span>}
                        </td>
                        <td className="py-2 text-right text-xs text-zinc-500">{o.last_call_at ? new Date(o.last_call_at).toLocaleString() : "—"}</td>
                      </tr>
                      {expanded && o.providers?.length > 0 && (
                        <tr className="text-xs text-zinc-500">
                          <td colSpan={5} className="py-1 pl-6">
                            By provider:{" "}
                            {o.providers.map(p => (
                              <span key={p.provider} className="mr-3"><code>{p.provider}</code> {USD(p.cost_usd)}
                                {p.unpriced_calls > 0 && <span className="text-amber-600"> +{NUM(p.unpriced_calls)} unpriced</span>}
                              </span>
                            ))}
                          </td>
                        </tr>
                      )}
                      {expanded && o.features.map(f => (
                        <tr key={f.feature} className="text-xs text-zinc-500">
                          <td className="py-1 pl-6"><code>{f.feature || "(unnamed)"}</code></td>
                          <td className="py-1 text-right">{NUM(f.calls)}</td>
                          <td />
                          <td className="py-1 text-right">{USD(f.cost_usd)}</td>
                          <td />
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function AlertsCard() {
  const [alerts, setAlerts] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    api.getAdminAlerts()
      .then(a => { setAlerts(a); setError(null); })
      .catch(e => { setAlerts([]); setError(e.response?.data?.detail || e.message); });
  }, []);
  useEffect(load, [load]);

  const resolve = async (id) => {
    await api.resolveAdminAlert(id).catch(() => {});
    setAlerts(a => a.filter(x => x.id !== id));
  };

  return (
    <div className="bg-white rounded-xl border border-zinc-100 p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle size={18} className="text-red-500" />
        <h2 className="font-semibold text-zinc-900">Open system alerts</h2>
        <button onClick={load} className="ml-auto text-zinc-400 hover:text-zinc-600" title="Refresh"><RefreshCw size={14} /></button>
      </div>
      {alerts === null && <p className="text-sm text-zinc-500">Loading…</p>}
      {error && <p className="text-sm text-red-500">{error}</p>}
      {!error && alerts?.length === 0 && <p className="flex items-center gap-2 text-sm text-green-600"><Check size={14} /> No open alerts.</p>}
      <ul className="space-y-2">
        {alerts?.map(a => (
          <li key={a.id} className="flex items-start gap-3 rounded-lg border border-zinc-100 p-3 hover:bg-zinc-50">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-zinc-900">
                {KIND_LABELS[a.kind] || a.kind} — <code>{a.model || "?"}</code>
                {a.feature && <span className="font-normal text-zinc-500"> · {a.feature}</span>}
              </p>
              <p className="text-xs text-zinc-500 break-words">{a.message}</p>
              <p className="text-xs text-zinc-400 mt-1">
                {a.count}× · last {new Date(a.last_seen).toLocaleString()}
                {a.org_id && <> · org {a.org_id}</>}
                {a.suggestion && <> · provider suggests <code>{a.suggestion}</code></>}
              </p>
            </div>
            <button onClick={() => resolve(a.id)} className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-100">
              <Check size={14} /> Resolve
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AdminPage() {
  const { loaded, isAdmin, userId, configured, error } = usePlatformAdmin();
  if (!loaded) return null;
  // Signed-in non-admins see their own id instead of a silent redirect — the usual cause is
  // PLATFORM_ADMIN_USER_IDS holding the dev-instance id while production uses the live Clerk instance.
  if (!isAdmin) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="bg-white rounded-xl border border-zinc-100 p-5 shadow-sm text-sm text-zinc-600 space-y-2">
          <p className="font-semibold text-zinc-900">Not a platform admin</p>
          {error ? (
            <p>Could not check admin status ({String(error)}).</p>
          ) : (
            <p>
              Your Clerk user id is <code className="select-all rounded bg-zinc-100 px-1">{userId}</code>. Set{" "}
              <code>{'{"role": "platform_admin"}'}</code> in this user&apos;s <b>private</b> metadata in the Clerk dashboard
              (applies within 5 minutes), or add the id to <code>PLATFORM_ADMIN_USER_IDS</code> on the backend and redeploy.
            </p>
          )}
          {configured === 0 && (
            <p className="text-zinc-400">
              The backend loaded no ids from <code>PLATFORM_ADMIN_USER_IDS</code>, so only Clerk metadata can grant access.
            </p>
          )}
          {configured > 0 && (
            <p className="text-zinc-400">The backend has {configured} admin id(s) configured, none of them yours.</p>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="p-8 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-zinc-900">Admin</h1>
        <p className="text-sm text-zinc-500">Founder-only. Customers never see this page or these alerts.</p>
      </div>
      <ProviderBudgetsCard />
      <ModelsCard />
      <AlertsCard />
      <UsageCard />
    </div>
  );
}
